import { Alert, AlertIcon, Button, ButtonProps, forwardRef, useToast } from '@chakra-ui/react';
import { EventType } from '@waitroom/analytics';
import { selectCurrentUserSubscriptionPlan, useAuthStore, useHasAccess } from '@waitroom/auth';
import { isFreePlan, useActivateTrial, useHostOptIn } from '@waitroom/common';
import { Role } from '@waitroom/models';
import { getRequestData } from '@waitroom/react-query';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../../constants/routes';
import { analyticsService } from '../../../../analytics/services';

const HostButton = forwardRef(
  (
    {
      onSuccess,
      onMutate,
      ...rest
    }: ButtonProps & {
      onSuccess?: () => Promise<void> | void;
      onMutate?: () => Promise<void> | void;
    },
    ref,
  ): ReactElement | null => {
    const { t } = useTranslation();
    const toast = useToast();
    const plan = useAuthStore(selectCurrentUserSubscriptionPlan);
    const { userId, accessToken } = useAuthStore();
    const { access } = useHasAccess(Role.SESSION_OWNER);
    const hasAccess = access[Role.SESSION_OWNER];

    const { isPending: isPendingActivateTrial, mutate: mutateActivateTrial } = useActivateTrial({
      onMutate,
      onSuccess: (response) => {
        const usp = getRequestData(response);
        if (!usp) return;
        if (usp?.status === 'trialing') {
          analyticsService.track(EventType.SubscriptionFreeTrialStarted, {
            isNewHost: false,
          });
        }
        if (onSuccess) onSuccess();
      },
      onError: () => {
        toast({
          position: 'top',
          duration: 6000,
          render: () => (
            <Alert status="error" fontWeight="bold" fontSize="lg" p={2}>
              <AlertIcon />
              {t('error.defaultTitle')}
            </Alert>
          ),
        });
      },
    });

    const { isPending: isPendingHostOptIn, mutate: mutateHostOptIn } = useHostOptIn({
      onSuccess: () => {
        analyticsService.track(EventType.HostOptedIn, {});
        mutateActivateTrial();
      },
      onError: () => {
        toast({
          position: 'top',
          duration: 6000,
          render: () => (
            <Alert status="error" fontWeight="bold" fontSize="lg" p={2}>
              <AlertIcon />
              {t('error.defaultTitle')}
            </Alert>
          ),
        });
      },
    });
    const navigate = useNavigate();
    const isPending = isPendingActivateTrial || isPendingHostOptIn;

    // skip if not logged in
    if (!accessToken || !userId) return null;
    // link to dashboard if already has access and subscribed
    if (plan && (plan.trialEndsAt || !isFreePlan(plan))) {
      return hasAccess ? (
        <Button
          {...rest}
          type="button"
          ref={ref}
          isLoading={isPending}
          onClick={async () => {
            if (onSuccess) await onSuccess();
            navigate(routes.DASHBOARD.DEFAULT.link);
          }}
        />
      ) : (
        <Button
          {...rest}
          type="button"
          ref={ref}
          onClick={() => mutateHostOptIn()}
          isLoading={isPending}
        />
      );
    }
    return (
      <Button
        {...rest}
        type="button"
        ref={ref}
        onClick={() => {
          mutateHostOptIn();
        }}
        isLoading={isPending}
      />
    );
  },
);

export default HostButton;
